/**
 * Active Bill of Materials storefront store.
 *
 * - Persists the draft + guest edit token in localStorage
 * - Debounced autosave to the app-proxy BoM API (`/apps/c3checkout/bom`)
 * - Merges duplicate variant / identical configured lines
 * - Loads `?bom=<public-id>` share links (read-only without edit authority)
 * - Signed-in customers can list / load / delete account BoMs and update
 *   without the guest edit token
 * - Emits `bom:change` and `bom:save` CustomEvents on `document`
 */

export const BOM_CHANGE_EVENT = 'bom:change';
export const BOM_SAVE_EVENT = 'bom:save';

/**
 * Open the storefront account sheet (same control as the header).
 * Falls back to Shopify's customer authentication URL when the sheet is missing.
 * @param {string} [fallbackUrl]
 * @returns {boolean}
 */
export function openCustomerSignIn(fallbackUrl = '') {
  const accounts = [...document.querySelectorAll('shopify-account')];
  const isVisible = (el) => {
    if (!(el instanceof HTMLElement)) return false;
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && el.getClientRects().length > 0;
  };

  const account =
    accounts.find((el) => el.closest('pa-header') && isVisible(el)) ||
    accounts.find(isVisible) ||
    accounts[0];

  const trigger =
    account?.shadowRoot?.querySelector('[part~="signed-out-avatar"]') ||
    account?.shadowRoot?.querySelector('button, [role="button"]');

  if (trigger instanceof HTMLElement) {
    trigger.click();
    return true;
  }

  if (fallbackUrl) {
    window.location.assign(fallbackUrl);
    return true;
  }

  if (account instanceof HTMLElement) {
    account.click();
    return true;
  }

  return false;
}

const STORAGE_KEY = 'pa-bom-draft-v1';
const AUTOSAVE_MS = 750;
const MAX_ROWS = 200;
const REMOTE_UNAVAILABLE_STATUSES = new Set([404, 501, 502, 503]);

/** @enum {string} */
export const BomLineKind = {
  VARIANT: 'variant',
  CONFIGURED: 'configured',
  UNRESOLVED: 'unresolved',
};

/** @enum {string} */
export const BomLineStatus = {
  PENDING: 'pending',
  FOUND: 'found',
  UNAVAILABLE: 'unavailable',
  NOT_FOUND: 'not-found',
};

/** @enum {string} */
export const BomSaveState = {
  IDLE: 'idle',
  DIRTY: 'dirty',
  SAVING: 'saving',
  SAVED: 'saved',
  ERROR: 'error',
};

/**
 * @typedef {object} BomLine
 * @property {string} id
 * @property {'variant'|'configured'|'unresolved'} kind
 * @property {'pending'|'found'|'unavailable'|'not-found'} status
 * @property {string} sku
 * @property {number} quantity
 * @property {number|null} variantId
 * @property {number|null} [productId]
 * @property {string} [productHandle]
 * @property {string} [vendor]
 * @property {string} title
 * @property {number|null} price
 * @property {Record<string, string>|null} [properties]
 */

/**
 * @typedef {object} BomDraft
 * @property {string|null} id
 * @property {string|null} editToken
 * @property {string} title
 * @property {BomLine[]} lines
 * @property {boolean} canEdit
 * @property {string|null} updatedAt
 */

function randomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  }
  return `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function mergeKey(line) {
  if (line.kind === BomLineKind.CONFIGURED) {
    const catalog = line.properties?._catalogNumber || line.sku || '';
    const cfg = line.properties?._cfg || '';
    return `c:${catalog.toLowerCase()}:${cfg}`;
  }
  if (line.kind === BomLineKind.VARIANT && line.variantId != null) {
    return `v:${line.variantId}`;
  }
  const sku = (line.sku || '').trim().toLowerCase();
  return `u:${sku || line.id}`;
}

function cloneLine(line) {
  return {
    id: line.id || randomId(),
    kind: line.kind || BomLineKind.UNRESOLVED,
    status: line.status || BomLineStatus.PENDING,
    sku: line.sku || '',
    quantity: Math.max(1, Math.floor(Number(line.quantity) || 1)),
    variantId: line.variantId ?? null,
    productId: line.productId ?? null,
    productHandle: line.productHandle || '',
    vendor: line.vendor || '',
    title: line.title || '',
    price: line.price ?? null,
    properties: line.properties ? { ...line.properties } : null,
  };
}

function apiBase() {
  const route = globalThis.Theme?.routes?.bom_url;
  return route || '/apps/c3checkout/bom';
}

function pageUrl() {
  return globalThis.Theme?.routes?.bom_page_url || '/pages/quick-order';
}

/**
 * Stable snapshot of the payload we autosave, used to skip no-op saves.
 * @param {{title?: string, lines: BomLine[]}} draft
 */
function draftFingerprint(draft) {
  return JSON.stringify({
    title: draft.title || '',
    lines: (draft.lines || []).map((line) => ({
      id: line.id,
      kind: line.kind,
      status: line.status,
      sku: line.sku,
      quantity: line.quantity,
      variantId: line.variantId ?? null,
      productId: line.productId ?? null,
      productHandle: line.productHandle || '',
      vendor: line.vendor || '',
      title: line.title || '',
      price: line.price ?? null,
      properties: line.properties || null,
    })),
  });
}

class BomStore {
  /** @type {BomDraft} */
  #draft = {
    id: null,
    editToken: null,
    title: '',
    lines: [],
    canEdit: true,
    updatedAt: null,
  };

  /** @type {string} */
  #saveState = BomSaveState.IDLE;

  /** @type {string|null} */
  #saveError = null;

  /** @type {ReturnType<typeof setTimeout>|null} */
  #saveTimer = null;

  /** @type {Promise<void>|null} */
  #savePromise = null;

  /** @type {boolean} */
  #ready = false;

  /** @type {boolean} */
  #shareMode = false;

  /** @type {Promise<void>|null} */
  #initPromise = null;

  /** Fingerprint of the last payload successfully saved. */
  #syncedFingerprint = '';

  /**
   * When the app proxy is missing (404) or the API is down, stop hammering
   * the same payload. A real local edit (new fingerprint) or flush/share retries.
   * @type {boolean}
   */
  #remoteUnavailable = false;

  /** Fingerprint that produced the last remote-unavailable failure. */
  #failedFingerprint = '';

  get ready() {
    return this.#ready;
  }

  get draft() {
    return {
      ...this.#draft,
      lines: this.#draft.lines.map((line) => cloneLine(line)),
    };
  }

  get lines() {
    return this.#draft.lines.map((line) => cloneLine(line));
  }

  get lineCount() {
    return this.#draft.lines.length;
  }

  get itemCount() {
    return this.#draft.lines.reduce((sum, line) => sum + line.quantity, 0);
  }

  get canEdit() {
    return this.#draft.canEdit !== false;
  }

  get isShareView() {
    return this.#shareMode && !this.canEdit;
  }

  get saveState() {
    return this.#saveState;
  }

  get saveError() {
    return this.#saveError;
  }

  get publicId() {
    return this.#draft.id;
  }

  get title() {
    return this.#draft.title || '';
  }

  get customerLoggedIn() {
    return globalThis.Theme?.customerLoggedIn === true;
  }

  get shareUrl() {
    if (!this.#draft.id) return '';
    const url = new URL(pageUrl(), window.location.origin);
    url.searchParams.set('bom', this.#draft.id);
    return url.toString();
  }

  /**
   * Initialize from localStorage and optional `?bom=` query. Safe to call
   * multiple times; subsequent calls await the first init.
   * @returns {Promise<void>}
   */
  init() {
    if (this.#initPromise) return this.#initPromise;
    this.#initPromise = this.#doInit();
    return this.#initPromise;
  }

  async #doInit() {
    this.#loadLocal();

    const params = new URLSearchParams(window.location.search);
    const sharedId = params.get('bom');

    if (sharedId) {
      this.#shareMode = true;
      try {
        await this.#fetchRemote(sharedId);
      } catch (error) {
        console.warn('[bom-store] failed to load shared BoM:', error);
        this.#setSaveState(BomSaveState.ERROR, error?.message || 'Could not load this BoM.');
      }
    } else if (this.#draft.id && (this.#draft.editToken || this.customerLoggedIn)) {
      // Refresh from server when we have credentials, falling back to local.
      try {
        await this.#fetchRemote(this.#draft.id);
      } catch {
        // Keep local draft offline.
      }
    }

    this.#ready = true;
    this.#emitChange();
  }

  /**
   * Replace the entire line list (used by the BoM builder after edits).
   * @param {BomLine[]} lines
   * @param {{title?: string, immediate?: boolean}} [options]
   */
  setLines(lines, options = {}) {
    if (!this.canEdit) return;

    const nextLines = lines.map((line) => cloneLine(line)).slice(0, MAX_ROWS);
    const nextTitle = typeof options.title === 'string' ? options.title : this.#draft.title;
    const nextFingerprint = draftFingerprint({ title: nextTitle, lines: nextLines });
    const currentFingerprint = draftFingerprint(this.#draft);

    if (nextFingerprint === currentFingerprint) {
      // No-op sync from the builder — don't re-emit or re-schedule autosave.
      return;
    }

    this.#draft.lines = nextLines;
    if (typeof options.title === 'string') this.#draft.title = options.title;
    this.#persistLocal();
    this.#emitChange();
    this.#scheduleSave(options.immediate);
  }

  /**
   * Add or merge a line into the active BoM.
   * @param {Partial<BomLine>} input
   * @returns {BomLine|null}
   */
  addLine(input) {
    if (!this.canEdit) return null;

    const line = cloneLine({
      id: randomId(),
      kind: BomLineKind.UNRESOLVED,
      status: BomLineStatus.PENDING,
      sku: '',
      quantity: 1,
      variantId: null,
      title: '',
      price: null,
      properties: null,
      ...input,
    });

    const key = mergeKey(line);
    const existing = this.#draft.lines.find((row) => mergeKey(row) === key);

    if (existing) {
      existing.quantity += line.quantity;
      if (line.status === BomLineStatus.FOUND || line.status === BomLineStatus.UNAVAILABLE) {
        existing.status = line.status;
        existing.variantId = line.variantId ?? existing.variantId;
        existing.title = line.title || existing.title;
        existing.price = line.price ?? existing.price;
        existing.productHandle = line.productHandle || existing.productHandle;
        existing.productId = line.productId ?? existing.productId;
        existing.vendor = line.vendor || existing.vendor;
        if (line.properties) existing.properties = { ...line.properties };
      }
      this.#persistLocal();
      this.#emitChange({ added: true });
      this.#scheduleSave();
      return cloneLine(existing);
    }

    if (this.#draft.lines.length >= MAX_ROWS) {
      this.#setSaveState(BomSaveState.ERROR, `BoMs are limited to ${MAX_ROWS} rows.`);
      this.#emitChange();
      return null;
    }

    this.#draft.lines.push(line);
    this.#persistLocal();
    this.#emitChange({ added: true });
    this.#scheduleSave();
    return cloneLine(line);
  }

  /**
   * @param {string} lineId
   * @param {number} quantity
   */
  updateQuantity(lineId, quantity) {
    if (!this.canEdit) return;
    const line = this.#draft.lines.find((row) => row.id === lineId);
    if (!line) return;
    line.quantity = Math.max(1, Math.floor(Number(quantity) || 1));
    this.#persistLocal();
    this.#emitChange();
    this.#scheduleSave();
  }

  /**
   * @param {string} lineId
   * @param {Partial<BomLine>} patch
   */
  updateLine(lineId, patch) {
    if (!this.canEdit) return;
    const line = this.#draft.lines.find((row) => row.id === lineId);
    if (!line) return;
    Object.assign(line, cloneLine({ ...line, ...patch, id: line.id }));
    this.#persistLocal();
    this.#emitChange();
    this.#scheduleSave();
  }

  /** @param {string} lineId */
  removeLine(lineId) {
    if (!this.canEdit) return;
    this.#draft.lines = this.#draft.lines.filter((row) => row.id !== lineId);
    this.#persistLocal();
    this.#emitChange();
    this.#scheduleSave();
  }

  clear() {
    if (!this.canEdit) return;
    this.#draft.lines = [];
    this.#persistLocal();
    this.#emitChange();
    this.#scheduleSave(true);
  }

  /**
   * Start a fresh editable BoM (drops share-view lock). Keeps nothing remote.
   */
  startNew() {
    this.#shareMode = false;
    this.#draft = {
      id: null,
      editToken: null,
      title: '',
      lines: [],
      canEdit: true,
      updatedAt: null,
    };
    this.#syncedFingerprint = '';
    this.#remoteUnavailable = false;
    this.#failedFingerprint = '';
    this.#persistLocal();
    this.#setSaveState(BomSaveState.IDLE);
    this.#emitChange();
  }

  /**
   * @param {string} title
   */
  setTitle(title) {
    if (!this.canEdit) return;
    const next = typeof title === 'string' ? title : '';
    if (next === this.#draft.title) return;
    this.#draft.title = next;
    this.#persistLocal();
    this.#scheduleSave();
  }

  /**
   * @returns {Promise<Array<{id: string, title: string, lineCount: number, updatedAt: string, createdAt: string}>>}
   */
  async listAccountBoms() {
    if (!this.customerLoggedIn) return [];

    const response = await fetch(apiBase(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const body = await response.json().catch(() => ({}));
    if (response.status === 401) return [];
    if (REMOTE_UNAVAILABLE_STATUSES.has(response.status) || response.status === 405) {
      return [];
    }
    if (!response.ok) {
      throw new Error(body.error || `Could not load saved BoMs (${response.status}).`);
    }
    return Array.isArray(body.boms) ? body.boms : [];
  }

  /**
   * Replace the active draft with a BoM from the signed-in customer's account.
   * @param {string} publicId
   * @returns {Promise<void>}
   */
  async loadAccountBom(publicId) {
    if (!publicId) throw new Error('Missing BoM id.');
    this.#shareMode = false;
    await this.#fetchRemote(publicId);
    this.#persistLocal();
    this.#emitChange();
  }

  /**
   * @param {string} publicId
   * @returns {Promise<void>}
   */
  async deleteAccountBom(publicId) {
    if (!publicId) throw new Error('Missing BoM id.');

    const headers = { Accept: 'application/json' };
    if (this.#draft.editToken && this.#draft.id === publicId) {
      headers['X-Bom-Edit-Token'] = this.#draft.editToken;
    }

    const response = await fetch(`${apiBase()}/${encodeURIComponent(publicId)}`, {
      method: 'DELETE',
      headers,
      credentials: 'same-origin',
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body.error || `Could not delete BoM (${response.status}).`);
    }

    if (this.#draft.id === publicId) this.startNew();
  }

  /** Force an immediate autosave. @returns {Promise<void>} */
  async flush() {
    if (this.#saveTimer) {
      clearTimeout(this.#saveTimer);
      this.#saveTimer = null;
    }
    // Share / explicit flush may retry even after the proxy was unavailable.
    this.#remoteUnavailable = false;
    return this.#saveNow();
  }

  /** @returns {Promise<string>} */
  async ensureShareUrl() {
    await this.flush();
    if (!this.#draft.id) {
      throw new Error(this.#saveError || 'Could not create a share link.');
    }
    return this.shareUrl;
  }

  subscribe(listener) {
    const handler = () => listener(this.draft, this.saveState, this.saveError);
    document.addEventListener(BOM_CHANGE_EVENT, handler);
    document.addEventListener(BOM_SAVE_EVENT, handler);
    return () => {
      document.removeEventListener(BOM_CHANGE_EVENT, handler);
      document.removeEventListener(BOM_SAVE_EVENT, handler);
    };
  }

  #loadLocal() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      this.#draft.id = typeof parsed.id === 'string' ? parsed.id : null;
      this.#draft.editToken = typeof parsed.editToken === 'string' ? parsed.editToken : null;
      this.#draft.title = typeof parsed.title === 'string' ? parsed.title : '';
      this.#draft.lines = Array.isArray(parsed.lines)
        ? parsed.lines.map((line) => cloneLine(line)).slice(0, MAX_ROWS)
        : [];
      this.#draft.canEdit = true;
      this.#draft.updatedAt = parsed.updatedAt || null;
    } catch {
      // Ignore corrupt storage.
    }
  }

  #persistLocal() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          id: this.#draft.id,
          editToken: this.#draft.editToken,
          title: this.#draft.title,
          lines: this.#draft.lines,
          updatedAt: this.#draft.updatedAt,
        }),
      );
    } catch {
      // Quota / private mode — continue with in-memory only.
    }
  }

  async #fetchRemote(publicId) {
    const headers = { Accept: 'application/json' };
    if (this.#draft.editToken && this.#draft.id === publicId) {
      headers['X-Bom-Edit-Token'] = this.#draft.editToken;
    }

    const response = await fetch(`${apiBase()}/${encodeURIComponent(publicId)}`, {
      headers,
      credentials: 'same-origin',
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body.error || `Could not load BoM (${response.status}).`);
    }

    const remoteIsOurs =
      body.canEdit === true ||
      (this.#draft.editToken && this.#draft.id === publicId);

    // Opening someone else's share link: show remote read-only and don't
    // overwrite our local editable draft credentials.
    if (this.#shareMode && !remoteIsOurs) {
      this.#draft = {
        id: body.id,
        editToken: null,
        title: body.title || '',
        lines: Array.isArray(body.lines) ? body.lines.map((line) => cloneLine(line)) : [],
        canEdit: false,
        updatedAt: body.updatedAt || null,
      };
      this.#setSaveState(BomSaveState.SAVED);
      return;
    }

    const previousId = this.#draft.id;
    this.#draft.id = body.id;
    this.#draft.title = body.title || this.#draft.title || '';
    this.#draft.lines = Array.isArray(body.lines)
      ? body.lines.map((line) => cloneLine(line))
      : this.#draft.lines;
    this.#draft.canEdit = body.canEdit !== false;
    this.#draft.updatedAt = body.updatedAt || null;
    if (body.editToken) {
      this.#draft.editToken = body.editToken;
    } else if (previousId !== body.id) {
      this.#draft.editToken = null;
    }
    this.#syncedFingerprint = draftFingerprint(this.#draft);
    this.#persistLocal();
    this.#setSaveState(BomSaveState.SAVED);
  }

  #scheduleSave(immediate = false) {
    if (!this.canEdit) return;

    const fingerprint = draftFingerprint(this.#draft);
    if (!immediate && fingerprint === this.#syncedFingerprint) return;
    if (!immediate && this.#remoteUnavailable) {
      if (fingerprint === this.#failedFingerprint) {
        // Same payload already failed — keep local draft, don't spam the proxy.
        this.#setSaveState(
          BomSaveState.ERROR,
          this.#saveError || 'Saved on this device only. Cloud sync unavailable.',
        );
        return;
      }
      // Content changed since the failure; allow one more remote attempt.
      this.#remoteUnavailable = false;
    }

    this.#setSaveState(BomSaveState.DIRTY);
    if (this.#saveTimer) clearTimeout(this.#saveTimer);
    if (immediate) {
      this.#saveNow();
      return;
    }
    this.#saveTimer = setTimeout(() => {
      this.#saveTimer = null;
      this.#saveNow();
    }, AUTOSAVE_MS);
  }

  async #saveNow() {
    if (!this.canEdit) return;
    if (this.#savePromise) {
      await this.#savePromise;
      // If the draft changed while a save was in flight, queue one more pass.
      if (
        this.canEdit &&
        !this.#remoteUnavailable &&
        draftFingerprint(this.#draft) !== this.#syncedFingerprint
      ) {
        return this.#saveNow();
      }
      return;
    }

    this.#savePromise = this.#performSave().finally(() => {
      this.#savePromise = null;
    });
    return this.#savePromise;
  }

  async #performSave() {
    const fingerprint = draftFingerprint(this.#draft);
    this.#setSaveState(BomSaveState.SAVING);

    const payload = {
      title: this.#draft.title,
      lines: this.#draft.lines,
    };

    try {
      let response;
      const canUpdateRemote =
        Boolean(this.#draft.id) &&
        (Boolean(this.#draft.editToken) || (this.customerLoggedIn && this.canEdit));

      if (canUpdateRemote) {
        /** @type {Record<string, string>} */
        const headers = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        };
        if (this.#draft.editToken) headers['X-Bom-Edit-Token'] = this.#draft.editToken;
        response = await fetch(`${apiBase()}/${encodeURIComponent(this.#draft.id)}`, {
          method: 'PUT',
          headers,
          credentials: 'same-origin',
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(apiBase(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify(payload),
        });
      }

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (REMOTE_UNAVAILABLE_STATUSES.has(response.status)) {
          this.#remoteUnavailable = true;
          this.#failedFingerprint = fingerprint;
          throw new Error('Saved on this device only. Cloud sync unavailable.');
        }
        throw new Error(body.error || `Save failed (${response.status}).`);
      }

      this.#remoteUnavailable = false;
      this.#failedFingerprint = '';
      this.#draft.id = body.id || this.#draft.id;
      if (body.editToken) this.#draft.editToken = body.editToken;
      this.#draft.updatedAt = body.updatedAt || new Date().toISOString();
      this.#draft.canEdit = true;
      this.#syncedFingerprint = fingerprint;
      this.#persistLocal();
      this.#setSaveState(BomSaveState.SAVED);
    } catch (error) {
      console.warn('[bom-store] autosave failed:', error);
      this.#setSaveState(BomSaveState.ERROR, error?.message || 'Could not save BoM.');
    }
  }

  #setSaveState(state, error = null) {
    this.#saveState = state;
    this.#saveError = error;
    document.dispatchEvent(
      new CustomEvent(BOM_SAVE_EVENT, {
        detail: { state, error, draft: this.draft },
      }),
    );
  }

  /**
   * @param {{added?: boolean}} [extra]
   */
  #emitChange(extra = {}) {
    document.dispatchEvent(
      new CustomEvent(BOM_CHANGE_EVENT, {
        detail: {
          draft: this.draft,
          lineCount: this.lineCount,
          itemCount: this.itemCount,
          canEdit: this.canEdit,
          isShareView: this.isShareView,
          saveState: this.saveState,
          ...extra,
        },
      }),
    );
  }
}

export const bomStore = new BomStore();

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bomStore.init(), { once: true });
  } else {
    bomStore.init();
  }
}
